import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { useTheme } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import AppPageHeader from "components/AppPageHeader";
import AppSearch from "components/AppSearch";
import ErrorBoundary from "components/ErrorBoundary";
import HorseListSettings from "components/HorseListSettings";
import Loader from "components/Loader/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import SearchNoResults from "components/SearchNoResults";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { SortOrder } from "interfaces/SortOrder";
import {
  setOrder as setStoreOrder,
  setOrderBy as setStoreOrderBy,
  setSearchQuery as setStoreSearchQuery,
  setTrainerSearchItem as setStoreTrainerSearchItem,
  setVetListHorsesRequestKey
} from "store/actions/vetHorseListPage";
import { MuiTheme } from "theme";
import AddHorseToTrack from "./AddHorseToTrack";
import HorseList from "./HorseList";

import useStyles from "./styles";

const RECORDS_PER_PAGE = 75;
export const VET_HORSE_LIST_QUERY_KEY = "infinite-list-horses";

const VetHorseListPage = () => {
  const classes = useStyles();
  const theme = useTheme<MuiTheme>();

  const matchesDownSm = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true
  });
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { showErrorSnack } = useSnackbar();
  const { order, orderBy, searchQuery, isInfiniteScrollOn } = useAppSelector(
    state => {
      const { order, orderBy, searchQuery } = state.vetHorseListPage;
      const isInfiniteScrollOn = state.queriesConfig.isInfiniteScrollOn;

      return {
        order,
        orderBy,
        searchQuery,
        isInfiniteScrollOn
      };
    }
  );

  const [clearSearch, setClearSearch] = useState<boolean>(false);

  const { useInfiniteListHorses } = useRacehorse360Api();
  const { currentUser } = useLoggedInUser();

  let currentFacilityIds;
  if (currentUser.isVeterinarian) {
    currentFacilityIds = currentUser.vetFacilityIds;
  } else if (currentUser.isRacingOfficial) {
    currentFacilityIds = currentUser.racingOfficialFacilityIds;
  }

  const query: racehorse360.IHorseFilter = {
    horseOrTrainerName: searchQuery?.length
      ? { contains: searchQuery }
      : undefined,
    facilityIds: currentFacilityIds
  };

  const {
    isLoading: isListHorsesLoading,
    isFetching: isListHorsesFetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    data,
    compoundRequestKey
  } = useInfiniteListHorses(
    VET_HORSE_LIST_QUERY_KEY,
    {
      query: query,
      pagingOptions: {
        maxResults: RECORDS_PER_PAGE,
        includeSummary: true
      },
      getOptions: {
        select: [
          "name",
          "trainer.firstName",
          "trainer.middleName",
          "trainer.lastName",
          "trainer.fullName",
          "trainer.trainerNumber",
          "trainer.type",
          "stallNumber",
          "barn.name",
          "highSpeedFurlongs",
          "lastRaceDate",
          "lastWorkoutDate",
          "lastWorkDate",
          "healthWatchStatus",
          "riskFactorsCount",
          "riskFactorScore",
          "currentFacility"
        ],
        orderBy: orderBy.map(ob => `${ob} ${order}`)
      }
    },
    {
      onError: err => {
        showErrorSnack(err.message);
      }
    }
  );

  const horses: racehorse360.IHorse[] = data?.pages ? data.pages.flat() : [];

  useInfiniteScroll(
    tableBodyRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInfiniteScrollOn,
    true
  );

  const shouldShowAddHorseToTrackButton = !currentUser.isRegulatoryVet;
  const isDataLoading = [isListHorsesLoading, isListHorsesFetching].some(
    Boolean
  );
  const isMainLoaderShowing = [!isFetchingNextPage, isDataLoading].every(
    Boolean
  );

  const handleSort = useCallback(
    (newOrderBy: string[], order: SortOrder) => {
      dispatch(setStoreOrder(order));
      dispatch(setStoreOrderBy(newOrderBy));
    },
    [dispatch, order, orderBy]
  );

  const handleClear = useCallback(() => {
    dispatch(setStoreSearchQuery(""));
    dispatch(setStoreTrainerSearchItem(null));
    setClearSearch(true);
  }, [dispatch]);

  const handleAppSearch = (value: string) => {
    dispatch(setStoreSearchQuery(value));
  };

  useEffect(() => {
    return () => {
      dispatch(setStoreSearchQuery(""));
    };
  }, []);

  useEffect(() => {
    dispatch(setVetListHorsesRequestKey(compoundRequestKey));
  }, [JSON.stringify(compoundRequestKey)]);

  return (
    <AppPage>
      <AppPageHeader className={classes.header}>
        <Grid
          container
          alignItems={"center"}
          justifyContent={"space-between"}
          classes={{
            root: classes.gridRoot,
            "spacing-xs-3": classes.gridItem
          }}
        >
          <Grid item md={3} lg={3} />
          <Grid item xs={6} sm={6} md={6} lg={6}>
            <ErrorBoundary>
              <AppSearch
                isForceSearch
                className={classes.appSearch}
                placeholder="Search Horses and Trainers"
                onSearch={handleAppSearch}
                shouldMakeHorseOrTrainerRequest={true}
                clearSearch={clearSearch}
                onClearSearch={setClearSearch}
              />
            </ErrorBoundary>
          </Grid>
          <Grid
            container
            item
            xs={6}
            sm={6}
            md={3}
            lg={3}
            justifyContent={"flex-end"}
          >
            {matchesDownSm && (
              <ErrorBoundary>
                <HorseListSettings />
              </ErrorBoundary>
            )}
            {shouldShowAddHorseToTrackButton && <AddHorseToTrack />}
          </Grid>
        </Grid>
      </AppPageHeader>
      <AppPageContent className={classes.appPageContent}>
        <ErrorBoundary>
          {Boolean(horses?.length) && (
            <HorseList
              onSortClick={handleSort}
              horses={horses}
              order={order}
              orderBy={orderBy}
              tableBodyRef={tableBodyRef}
            />
          )}
          {!horses?.length && !isDataLoading && (
            <SearchNoResults value={searchQuery} onClearSearch={handleClear} />
          )}
        </ErrorBoundary>
        <Loader
          className={clsx(classes.infiniteLoader, {
            [classes.showInfiniteLoader]: isFetchingNextPage,
            [classes.showOverlayLoader]: isMainLoaderShowing
          })}
        />
      </AppPageContent>
    </AppPage>
  );
};

export default VetHorseListPage;
